"use client"

import { useState } from "react"
import { Eye, EyeOff, AlertCircle, CheckCircle, ChevronDown } from "lucide-react"
import SideSection from "../constant/SideSection"
import { Link, useNavigate } from "react-router-dom"
import { useSignup } from "../hooks/useRegister"
// import { useSignin } from "../hooks/useSignin"

// Country codes for phone picker
const countryCodes = [
  { code: "+234", country: "NG", flag: "🇳🇬", name: "Nigeria" },
  { code: "+1", country: "US", flag: "🇺🇸", name: "United States" },
  { code: "+44", country: "GB", flag: "🇬🇧", name: "United Kingdom" },
  { code: "+91", country: "IN", flag: "🇮🇳", name: "India" },
  { code: "+86", country: "CN", flag: "🇨🇳", name: "China" },
]

export default function RegisterForm() {
  const [fullName, setFullName] = useState("")
  const [email, setEmail] = useState("")
  const [mobileNumber, setMobileNumber] = useState("")
  const [selectedCountryCode, setSelectedCountryCode] = useState(countryCodes[0]) // Default to Nigeria
  const [showCountryDropdown, setShowCountryDropdown] = useState(false)
  const [countryQuery, setCountryQuery] = useState("")
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [agreeToTerms, setAgreeToTerms] = useState(false)
  const [fieldErrors, setFieldErrors] = useState({})
  const [successMessage, setSuccessMessage] = useState("")

  const navigate = useNavigate()
  const { signup, loading, error, clearError } = useSignup()
  // Only the commented-out social buttons below used this.
  // const { googleSignin, appleSignin } = useSignin()

  const filteredCountryCodes = countryQuery.trim()
    ? countryCodes.filter(
        (country) =>
          country.code.toLowerCase().includes(countryQuery.trim().toLowerCase()) ||
          country.name.toLowerCase().includes(countryQuery.trim().toLowerCase())
      )
    : countryCodes

  const emailRegex = /\S+@\S+\.\S+/
  const symbolRegex = /[!@#$%^&*(),.?":{}|<>]/
  const numberRegex = /\d/
  const uppercaseRegex = /[A-Z]/

  const getErrorMessage = (name, value) => {
    switch (name) {
      case "fullName":
        if (!value.trim()) {
          return "Full name is required"
        }
        break
      case "email": {
        const normalizedEmail = value.trim().toLowerCase()
        if (!normalizedEmail) {
          return "Email is required"
        } else if (!emailRegex.test(normalizedEmail)) {
          return "Invalid email address"
        }
        break
      }
      case "mobileNumber":
        if (!value.trim()) {
          return "Invalid phone number"
        } else if (value.trim().length < 10) {
          return "Invalid phone number"
        }
        break
      case "password":
        if (!value) {
          return "Password is required"
        } else if (value.length < 8) {
          return "Password must be at least 8 characters long"
        } else if (!numberRegex.test(value) || !symbolRegex.test(value) || !uppercaseRegex.test(value)) {
          return "Weak password: include uppercase letters, numbers, and symbols"
        }
        break
      case "confirmPassword":
        if (!value) {
          return "Please confirm your password"
        } else if (password !== value) {
          return "Passwords do not match"
        }
        break
      case "agreeToTerms":
        if (!value) {
          return "You must agree to continue"
        }
        break
      default:
        break
    }

    return ""
  }

  const handleInputChange = (name, value) => {
    if (error) clearError()
    if (successMessage) setSuccessMessage("")

    switch (name) {
      case "fullName":
        setFullName(value)
        break
      case "email":
        setEmail(value)
        break
      case "mobileNumber":
        setMobileNumber(value)
        break
      case "password":
        setPassword(value)
        break
      case "confirmPassword":
        setConfirmPassword(value)
        break
      default:
        break
    }

    const message = getErrorMessage(name, value)
    setFieldErrors((prev) => {
      const next = { ...prev }
      if (message) {
        next[name] = message
      } else {
        delete next[name]
      }
      if (name === "password" && confirmPassword && confirmPassword === value) {
        delete next.confirmPassword
      }
      if (name === "password" && confirmPassword && confirmPassword !== value) {
        next.confirmPassword = "Passwords do not match"
      }
      return next
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    clearError()
    setSuccessMessage("")

    const nextErrors = {}
    const fullNameError = getErrorMessage("fullName", fullName)
    const normalizedEmail = email.trim().toLowerCase()
    const emailError = getErrorMessage("email", normalizedEmail)
    const mobileError = getErrorMessage("mobileNumber", mobileNumber)
    const passwordError = getErrorMessage("password", password)
    const confirmError = getErrorMessage("confirmPassword", confirmPassword)
    const termsError = getErrorMessage("agreeToTerms", agreeToTerms)

    if (fullNameError) nextErrors.fullName = fullNameError
    if (emailError) nextErrors.email = emailError
    if (mobileError) nextErrors.mobileNumber = mobileError
    if (passwordError) nextErrors.password = passwordError
    if (confirmError) nextErrors.confirmPassword = confirmError
    if (termsError) nextErrors.agreeToTerms = termsError

    setFieldErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) return

    const fullPhoneNumber = selectedCountryCode.code + mobileNumber.trim()

    const formData = {
      fullName,
      email: normalizedEmail,
      mobile: fullPhoneNumber,
      password,
      confirmPassword,
      agreeToTerms,
    }

    const result = await signup(formData)

    if (result.success) {
      setSuccessMessage("Account created successfully! Please check your email for the verification code.")
      navigate(`/auth/verify-email?email=${encodeURIComponent(normalizedEmail)}`)

      setFullName("")
      setEmail("")
      setMobileNumber("")
      setPassword("")
      setConfirmPassword("")
      setAgreeToTerms(false)
      setFieldErrors({})
    }
  }

  const isFormValid = () => {
    return (
      fullName.trim() &&
      email.trim() &&
      mobileNumber.trim() &&
      password &&
      confirmPassword &&
      agreeToTerms &&
      Object.keys(fieldErrors).length === 0
    )
  }

  const passwordStrong = password.length >= 8 && numberRegex.test(password) && symbolRegex.test(password)
  const confirmPasswordMatch = confirmPassword && confirmPassword === password && passwordStrong

  const inputBase =
    "w-full rounded-lg border px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none transition focus:ring-2 focus:ring-offset-0"
  const errorInput = "border-red-300 focus:border-red-500 focus:ring-red-200"
  const normalInput = "border-gray-300 focus:border-[#7a0051] focus:ring-[#7a0051]/30 shadow-sm"

  return (
    <div className="flex min-h-screen overflow-y-hidden bg-white text-gray-900">
      <div className="hidden w-1/2 overflow-hidden lg:block">
        <SideSection imageUrl="/images/car4.jpg" />
      </div>

      <div className="flex min-h-screen flex-1 flex-col items-center justify-center px-4">
        <div className="w-full max-w-[36rem]">
          <div className="flex justify-center mb-6">
            <img src="/images/logo.png" className="w-20" alt="PULO Logo" />
          </div>
          <div className="text-center mb-6">
            <h1 className="mb-2 text-3xl font-bold text-gray-900">Create Your PULO Account</h1>
            <p className="text-sm text-gray-600 font-medium sm:text-base">
              Join PULO to discover unforgettable destinations, connect with trusted assistants, and book your perfect
              stay.
            </p>
          </div>

          {successMessage && (
            <div className="mb-6 flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-3">
              <CheckCircle className="mt-0.5 h-5 w-5 text-green-500" />
              <p className="text-sm text-green-700">{successMessage}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="mb-2 block text-sm  text-gray-800">Enter Full Name</label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => handleInputChange("fullName", e.target.value)}
                placeholder="Abdussalam Adebowale Jaji"
                className={`${inputBase} text-black font-medium  placeholder:font-medium placeholder:text-gray-500 ${fieldErrors.fullName ? errorInput : normalInput}`}
                disabled={loading}
              />
              {fieldErrors.fullName && (
                <p className="mt-2 flex items-center gap-1 text-sm text-red-600">
                  <AlertCircle className="h-4 w-4" />
                  {fieldErrors.fullName}
                </p>
              )}
            </div>

            <div>
              <label className="mb-2 block text-sm text-gray-800">Email</label>
              <div className={`rounded-lg ${fieldErrors.email ? "ring-1 ring-red-200" : "ring-1 ring-transparent"}`}>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  placeholder="abdulsimj5@gmail.com"
                  className={`${inputBase} text-black font-medium placeholder:font-medium placeholder:text-gray-500  ${fieldErrors.email ? errorInput : normalInput}`}
                  disabled={loading}
                />
              </div>
              {fieldErrors.email && (
                <p className="mt-2 flex items-center gap-1 text-sm text-red-600">
                  <AlertCircle className="h-4 w-4" />
                  {fieldErrors.email}
                </p>
              )}
            </div>

            <div>
              <label className="mb-2 block text-sm text-gray-800">Phone No</label>
              <div
                className={`relative flex items-center rounded-lg border bg-white shadow-sm ${
                  fieldErrors.mobileNumber
                    ? "border-red-300 ring-1 ring-red-200"
                    : "border-gray-300 focus-within:ring-2 focus-within:ring-[#7a0051]/30"
                }`}
              >
                <button
                  type="button"
                  onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                  className="flex items-center gap-2 px-3 py-3 text-sm font-medium text-gray-800 hover:bg-gray-50 focus:outline-none"
                  disabled={loading}
                >
                  <span className="text-lg leading-none">{selectedCountryCode.flag}</span>
                  <span className="tracking-wide">{selectedCountryCode.code}</span>
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                </button>

                {showCountryDropdown && (
                  <div className="absolute left-0 top-full z-10 mt-1 w-64 rounded-md border border-gray-300 bg-white shadow-lg">
                    <div className="border-b border-gray-200 px-3 py-2">
                      <input
                        type="text"
                        value={countryQuery}
                        onChange={(e) => setCountryQuery(e.target.value)}
                        placeholder="Search country"
                        className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-[#7a0051] focus:outline-none focus:ring-2 focus:ring-[#7a0051]/20"
                      />
                    </div>
                    <div className="max-h-60 overflow-auto">
                      {filteredCountryCodes.length ? (
                        filteredCountryCodes.map((country) => (
                          <button
                            key={country.code}
                            type="button"
                            onClick={() => {
                              setSelectedCountryCode(country)
                              setShowCountryDropdown(false)
                              setCountryQuery("")
                            }}
                            className="flex w-full items-center px-3 py-2 text-left hover:bg-gray-100"
                          >
                            <span className="mr-3 text-lg leading-none">{country.flag}</span>
                            <span className="mr-2 font-medium">{country.code}</span>
                            <span className="text-sm text-gray-600">{country.name}</span>
                          </button>
                        ))
                      ) : (
                        <p className="px-3 py-3 text-sm text-gray-500">No results found</p>
                      )}
                    </div>
                  </div>
                )}

                <input
                  type="tel"
                  value={mobileNumber}
                  onChange={(e) => handleInputChange("mobileNumber", e.target.value)}
                  placeholder="905 955 5425"
                  className="flex-1 border-none bg-transparent text-base text-gray-900 placeholder-black focus:outline-none focus:ring-0"
                  disabled={loading}
                />
              </div>
              {fieldErrors.mobileNumber && (
                <p className="mt-2 flex items-center gap-1 text-sm text-red-600">
                  <AlertCircle className="h-4 w-4" />
                  {fieldErrors.mobileNumber}
                </p>
              )}
            </div>

            <div>
              <label className="mb-2 block text-sm text-gray-800">Password</label>
              <div
                className={`relative rounded-lg ${fieldErrors.password ? "ring-1 ring-red-200" : "ring-1 ring-transparent"}`}
              >
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => handleInputChange("password", e.target.value)}
                  placeholder="Shamwilu12@"
                  className={`${inputBase} pr-12 text-black font-medium placeholder:text-gray-500 placeholder:font-medium  ${fieldErrors.password ? errorInput : normalInput}`}
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-500 hover:text-gray-700"
                  disabled={loading}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {fieldErrors.password && (
                <p className="mt-2 flex items-center gap-1 text-sm text-red-600">
                  <AlertCircle className="h-4 w-4" />
                  {fieldErrors.password}
                </p>
              )}
              {!fieldErrors.password && password && passwordStrong && (
                <p className="mt-2 flex items-center gap-1 text-sm text-emerald-600">
                  <CheckCircle className="h-4 w-4" />
                  Password strength - strong
                </p>
              )}
            </div>

            <div>
              <label className="mb-2 block text-sm text-gray-800">Confirm Password</label>
              <div
                className={`relative rounded-lg ${fieldErrors.confirmPassword ? "ring-1 ring-red-200" : "ring-1 ring-transparent"}`}
              >
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => handleInputChange("confirmPassword", e.target.value)}
                  placeholder="Shamwilu12@"
                  className={`${inputBase} pr-12 text-black font-medium placeholder:text-gray-500 placeholder:font-medium ${fieldErrors.confirmPassword ? errorInput : normalInput}`}
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-500 hover:text-gray-700"
                  disabled={loading}
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {fieldErrors.confirmPassword && (
                <p className="mt-2 flex items-center gap-1 text-sm text-red-600">
                  <AlertCircle className="h-4 w-4" />
                  {fieldErrors.confirmPassword}
                </p>
              )}
              {!fieldErrors.confirmPassword && confirmPasswordMatch && (
                <p className="mt-2 flex items-center gap-1 text-sm text-emerald-600">
                  <CheckCircle className="h-4 w-4" />
                  Password confirmed
                </p>
              )}
            </div>

            <div className="flex items-start gap-3 pt-2">
              <input
                type="checkbox"
                checked={agreeToTerms}
                onChange={(e) => {
                  setAgreeToTerms(e.target.checked)
                  const message = getErrorMessage("agreeToTerms", e.target.checked)
                  setFieldErrors((prev) => {
                    const next = { ...prev }
                    if (message) {
                      next.agreeToTerms = message
                    } else {
                      delete next.agreeToTerms
                    }
                    return next
                  })
                }}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-[#7a0051] focus:ring-[#7a0051]"
                disabled={loading}
              />
              <p className="text-sm text-black font-medium leading-relaxed">
                I agree to PULO{" "}
                <Link
                  to="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-[#7a0051] hover:underline"
                >
                  Terms of Service
                </Link>{" "}
                and consent to{" "}
                <Link
                  to="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-[#7a0051] hover:underline"
                >
                  Privacy Policy
                </Link>
              </p>
            </div>
            {fieldErrors.agreeToTerms && (
              <p className="flex items-center gap-1 text-sm text-red-600">
                <AlertCircle className="h-4 w-4" />
                {fieldErrors.agreeToTerms}
              </p>
            )}

            <button
              type="submit"
              disabled={loading || !isFormValid()}
              className="w-full rounded-lg bg-[#7a0051] py-3 text-base font-semibold text-white shadow-md transition hover:bg-[#640140] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#7a0051] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? "Creating Account..." : "Continue"}
            </button>

            {/* Google and Apple sign-in are hidden for now — email and password only.
<div className="flex items-center gap-3 text-xs text-gray-400">
              <div className="h-px flex-1 bg-gray-400" />
              <span className="text-gray-500">Or</span>
              <div className="h-px flex-1 bg-gray-400" />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <button
                type="button"
                onClick={googleSignin}
                disabled={loading}
                className="flex items-center justify-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-gray-700 shadow-sm transition hover:border-gray-300 hover:shadow disabled:opacity-60"
              >
                <img src="/icons/google.svg" alt="Google" className="h-5 w-5" />
                Sign in with Google
              </button>
              <button
                type="button"
                onClick={appleSignin}
                disabled={loading}
                className="flex items-center justify-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-gray-700 shadow-sm transition hover:border-gray-300 hover:shadow disabled:opacity-60"
              >
                <img src="/icons/apple.svg" alt="Apple" className="h-5 w-5" />
                Sign in with Apple
              </button>
            </div>
            */}

            <div className="text-center text-sm font-medium text-gray-700">
              Already have an account?{" "}
              <button
                type="button"
                onClick={() => navigate("/auth/login")}
                className="font-semibold text-[#7a0051] underline"
                disabled={loading}
              >
                Log in
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
